// 豆包-seedream-4.0图像生成服务 - 根据官方文档优化
import settingsService from './settingsService.js'
import { ImageProxyService } from './imageProxyService.js'

export class DoubaoImageServiceV2 {
  // 生成图像
  static async generateImage(prompt, options = {}) {
    // 直接使用通用图像生成配置
    const config = settingsService.getImageGenerationConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      throw new Error('图像生成API未配置')
    }

    try {
      console.log('🎨 开始调用豆包-seedream-4.0生成图像...')
      console.log('📝 提示词:', prompt.substring(0, 100) + '...')
      
      const response = await this.callDoubaoAPI(prompt, options, config)
      console.log('✅ 豆包图像生成成功')
      return response
    } catch (error) {
      console.error('❌ 豆包图像生成失败:', error)
      throw error
    }
  }

  // 调用豆包API - 根据官方文档优化
  static async callDoubaoAPI(prompt, options = {}, config) {
    // 确保使用正确的模型名称
    let modelName = config.model || "doubao-seedream-4-0-250828"
    
    // 如果配置中的模型名称不完整，使用完整的模型名称
    if (modelName === "doubao-seedream-4-0") {
      modelName = "doubao-seedream-4-0-250828"
      console.log('🔄 修正模型名称:', modelName)
    }
    
    // 处理图片比例 - 根据aspectRatio参数确定尺寸
    let imageSize = options.size || "2K"
    if (options.aspectRatio) {
      // 1:1 - 正方形（用于角色设计）
      if (options.aspectRatio === "1:1") {
        imageSize = "1024x1024" // 1:1 正方形（注意：用x不是*）
        console.log('📐 使用1:1比例（角色设计）:', imageSize)
      }
      // 16:9 - 宽屏（用于场景和分镜设计）
      else if (options.aspectRatio === "16:9") {
        imageSize = "1920x1080" // 16:9 宽屏（注意：用x不是*）
        console.log('📐 使用16:9比例（场景/分镜设计）:', imageSize)
      }
    }
    
    // 根据官方文档构建请求体
    const requestBody = {
      model: modelName,
      prompt: prompt,
      sequential_image_generation: "auto",
      sequential_image_generation_options: {
        max_images: options.maxImages || 1
      },
      response_format: "url",
      size: imageSize,
      stream: options.stream !== undefined ? options.stream : false,
      watermark: options.watermark !== undefined ? options.watermark : true
    }

    // 如果提供了参考图像，添加到请求中
    if (options.referenceImages && options.referenceImages.length > 0) {
      requestBody.image = options.referenceImages
    }

    console.log('📤 发送豆包API请求:', {
      model: requestBody.model,
      prompt: prompt.substring(0, 50) + '...',
      max_images: requestBody.sequential_image_generation_options.max_images,
      size: requestBody.size
    })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 120000) // 2分钟超时

    try {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.apiKey}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      console.log('📨 收到响应状态:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ API响应错误:', errorData)
        throw new Error(`豆包API调用失败: ${response.status} ${errorData.error?.message || response.statusText}`)
      }

      const data = await response.json()
      console.log('📨 豆包API响应:', data)

      // 处理响应数据 - 根据官方文档格式
      if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        // 返回第一张图像的URL
        const imageUrl = data.data[0].url
        console.log('✅ 获取到图像URL:', imageUrl)
        
        // 直接返回原始URL，让SmartImage组件处理加载策略
        console.log('🖼️ 返回原始URL，由SmartImage处理加载:', imageUrl)
        return imageUrl
      } else if (data.url) {
        // 直接返回URL
        const imageUrl = data.url
        console.log('✅ 获取到图像URL:', imageUrl)
        
        // 直接返回原始URL，让SmartImage组件处理加载策略
        console.log('🖼️ 返回原始URL，由SmartImage处理加载:', imageUrl)
        return imageUrl
      } else {
        console.error('❌ 响应格式不正确:', data)
        throw new Error('豆包API未返回图像URL')
      }
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('豆包API调用超时，请稍后重试')
      }
      throw error
    }
  }

  // 测试API连接
  static async testConnection() {
    // 直接使用通用图像生成配置
    const config = settingsService.getImageGenerationConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      throw new Error('图像生成API未配置')
    }

    try {
      console.log('🔍 测试豆包API连接...')
      
      const testPrompt = '一个简单的红色圆圈，白色背景，测试图片'
      const result = await this.callDoubaoAPI(testPrompt, {
        maxImages: 1,
        size: '1K'
      }, config)
      
      console.log('✅ 豆包API连接测试成功')
      return {
        success: true,
        message: '豆包API连接正常',
        testImageUrl: result
      }
    } catch (error) {
      console.error('❌ 豆包API连接测试失败:', error)
      return {
        success: false,
        message: `豆包API连接失败: ${error.message}`
      }
    }
  }

  // 构建场景图像提示词
  static buildSceneImagePrompt(scene, style, characters) {
    let prompt = scene.imagePrompt || scene.title
    
    // 添加角色信息
    if (characters && characters.length > 0) {
      const characterNames = characters.map(c => c.name).join(', ')
      prompt += `, featuring characters: ${characterNames}`
    }
    
    // 添加风格信息
    if (style) {
      prompt += `, ${style} style`
    }
    
    // 添加场景描述
    if (scene.description) {
      prompt += `, ${scene.description}`
    }
    
    // 添加背景信息
    if (scene.background) {
      prompt += `, background: ${scene.background}`
    }
    
    // 添加情绪氛围
    if (scene.mood) {
      prompt += `, mood: ${scene.mood}`
    }
    
    // 添加时间信息
    if (scene.timeOfDay) {
      prompt += `, time: ${scene.timeOfDay}`
    }
    
    // 添加地点信息
    if (scene.location) {
      prompt += `, location: ${scene.location}`
    }
    
    // 添加质量提示
    prompt += ', high quality, cinematic composition, dramatic lighting, detailed'
    
    return prompt
  }

  // 批量生成场景图像
  static async generateBatchSceneImages(scenes, style, characters, options = {}) {
    console.log('🎨 开始批量生成场景图像，场景数:', scenes.length)
    
    // 直接使用通用图像生成配置
    const config = settingsService.getImageGenerationConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      console.warn('⚠️ 图像生成API未配置，跳过图像生成')
      return scenes
    }

    const scenesWithImages = []
    
    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i]
      try {
        console.log(`🖼️ 生成场景 ${i + 1}/${scenes.length}: ${scene.title}`)
        
        // 构建图像提示词
        const imagePrompt = this.buildSceneImagePrompt(scene, style, characters)
        
        // 生成图像 - 场景使用16:9比例
        const imageUrl = await this.generateImage(imagePrompt, {
          ...options,
          maxImages: 1,
          aspectRatio: '16:9', // 场景使用16:9比例
          size: options.size || '2K'
        })
        
        scene.sceneImage = imageUrl
        console.log(`✅ 场景 ${i + 1} 图像生成完成`)
        
        // 添加延迟避免API限制
        if (i < scenes.length - 1) {
          await this.delay(1000)
        }
      } catch (error) {
        console.warn(`⚠️ 场景 ${i + 1} 图像生成失败:`, error.message)
        scene.sceneImage = ''
      }
      
      scenesWithImages.push(scene)
    }
    
    console.log('✅ 批量场景图像生成完成')
    return scenesWithImages
  }

  // 生成角色头像/立绘
  static async generateCharacterAvatar(character, style, options = {}) {
    const prompt = this.buildCharacterImagePrompt(character, style)
    return await this.generateImage(prompt, {
      ...options,
      maxImages: 1,
      aspectRatio: '1:1', // 角色使用1:1比例
      size: options.size || '1K'
    })
  }

  // 构建角色图像提示词
  static buildCharacterImagePrompt(character, style) {
    let prompt = character.imagePrompt || character.name
    
    // 添加外观描述
    if (character.appearance) {
      prompt += `, ${character.appearance}`
    }
    
    // 添加角色定位
    if (character.role || character.type) {
      prompt += `, ${character.role || character.type}`
    }
    
    // 添加风格
    if (style) {
      prompt += `, ${style} style`
    }
    
    // 添加质量提示 - 强调1:1构图
    prompt += ', character portrait, centered composition, 1:1 aspect ratio, high quality, detailed, professional'
    
    return prompt
  }

  // 生成分镜图
  static async generateStoryboardImage(storyboard, style, options = {}) {
    const prompt = this.buildStoryboardImagePrompt(storyboard, style)
    return await this.generateImage(prompt, {
      ...options,
      maxImages: 1,
      aspectRatio: '16:9', // 分镜使用16:9比例
      size: options.size || '2K'
    })
  }

  // 构建分镜图像提示词
  static buildStoryboardImagePrompt(storyboard, style) {
    let prompt = storyboard.imagePrompt || storyboard.description || 'Storyboard frame'
    
    // 添加场景信息
    if (storyboard.scene) {
      prompt += `, ${storyboard.scene}`
    }
    
    // 添加角色信息
    if (storyboard.characters && storyboard.characters.length > 0) {
      prompt += `, featuring: ${storyboard.characters.join(', ')}`
    }
    
    // 添加动作/情绪
    if (storyboard.action) {
      prompt += `, action: ${storyboard.action}`
    }
    
    // 添加风格
    if (style) {
      prompt += `, ${style} style`
    }
    
    // 添加质量提示 - 强调16:9构图
    prompt += ', cinematic composition, 16:9 aspect ratio, storyboard frame, high quality, detailed'
    
    return prompt
  }

  /**
   * 图生图 - 使用参考图像生成新图像
   * @param {string} prompt - 提示词，描述要修改的内容
   * @param {string|string[]} referenceImages - 参考图像URL（单图或多图数组）
   * @param {Object} options - 生成选项
   * @returns {Promise<string>} 生成的图像URL
   */
  static async generateImageWithReference(prompt, referenceImages, options = {}) {
    // 直接使用通用图像生成配置
    const config = settingsService.getImageGenerationConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      throw new Error('图像生成API未配置')
    }

    try {
      console.log('🎨 开始调用豆包图生图API...')
      console.log('📝 提示词:', prompt.substring(0, 100) + '...')
      
      // 处理参考图像格式
      let imageParam
      if (Array.isArray(referenceImages)) {
        // 多图模式
        imageParam = referenceImages
        console.log('🖼️ 使用多图参考模式，图片数:', referenceImages.length)
      } else {
        // 单图模式
        imageParam = referenceImages
        console.log('🖼️ 使用单图参考模式')
      }
      
      // 调用API，传递参考图像
      const response = await this.callDoubaoImageToImageAPI(prompt, imageParam, options, config)
      console.log('✅ 图生图成功')
      return response
    } catch (error) {
      console.error('❌ 图生图失败:', error)
      throw error
    }
  }

  /**
   * 调用豆包图生图API
   * @param {string} prompt - 提示词
   * @param {string|string[]} image - 参考图像
   * @param {Object} options - 选项
   * @param {Object} config - API配置
   */
  static async callDoubaoImageToImageAPI(prompt, image, options = {}, config) {
    // 确保使用正确的模型名称
    let modelName = config.model || "doubao-seedream-4-0-250828"
    
    if (modelName === "doubao-seedream-4-0") {
      modelName = "doubao-seedream-4-0-250828"
      console.log('🔄 修正模型名称:', modelName)
    }
    
    // 处理图片尺寸
    let imageSize = options.size || "2K"
    
    // 根据官方文档构建请求体
    const requestBody = {
      model: modelName,
      prompt: prompt,
      image: image, // 参考图像（单图或多图数组）
      size: imageSize,
      sequential_image_generation: options.sequentialGeneration || "disabled",
      stream: options.stream !== undefined ? options.stream : false,
      response_format: "url",
      watermark: options.watermark !== undefined ? options.watermark : true
    }

    // 如果有额外的选项
    if (options.sequentialGeneration === "auto" && options.maxImages) {
      requestBody.sequential_image_generation_options = {
        max_images: options.maxImages
      }
    }

    console.log('📤 发送豆包图生图API请求:', {
      model: requestBody.model,
      prompt: prompt.substring(0, 50) + '...',
      imageType: Array.isArray(image) ? `多图(${image.length}张)` : '单图',
      size: requestBody.size,
      apiUrl: config.apiUrl
    })
    
    // 详细调试：打印 image 参数的前100个字符
    if (image) {
      if (Array.isArray(image)) {
        console.log('🔍 调试 - 多图参数:')
        image.forEach((img, index) => {
          if (typeof img === 'string') {
            const preview = img.substring(0, 100) + (img.length > 100 ? '...' : '')
            console.log(`  图${index + 1}:`, preview)
            console.log(`  图${index + 1}类型:`, img.startsWith('data:') ? 'Base64' : img.startsWith('http') ? 'HTTP URL' : img.startsWith('blob:') ? 'Blob URL (错误!)' : '未知')
          } else {
            console.log(`  图${index + 1}:`, typeof img, img)
          }
        })
      } else if (typeof image === 'string') {
        const preview = image.substring(0, 100) + (image.length > 100 ? '...' : '')
        console.log('🔍 调试 - 单图参数:', preview)
        console.log('🔍 调试 - 图片类型:', 
          image.startsWith('data:') ? 'Base64 ✅' : 
          image.startsWith('http') ? 'HTTP URL ✅' : 
          image.startsWith('blob:') ? '❌ Blob URL (不支持!)' : 
          '未知类型 ❌'
        )
      } else {
        console.error('❌ image 参数类型错误:', typeof image, image)
      }
    } else {
      console.error('❌ image 参数为空或undefined!')
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 600000) // 10分钟超时（图生图需要更长时间）

    try {
      console.log('⏱️ 开始调用图生图API，预计需要1-5分钟，请耐心等待...')
      console.log('📋 完整请求体:', JSON.stringify(requestBody, null, 2).substring(0, 500) + '...')
      
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.apiKey}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      console.log('📨 收到响应状态:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ API响应错误:', errorData)
        
        // 详细的错误信息
        const errorMessage = errorData.error?.message || response.statusText
        
        // 检查是否是内容审核问题
        if (errorMessage.includes('sensitive information') || errorMessage.includes('敏感信息')) {
          throw new Error('⚠️ 内容审核未通过：提示词或参考图可能包含敏感内容，请修改后重试')
        }
        
        // 检查是否是参数错误
        if (response.status === 400) {
          throw new Error(`❌ 请求参数错误: ${errorMessage}`)
        }
        
        throw new Error(`豆包图生图API调用失败: ${response.status} ${errorMessage}`)
      }

      const data = await response.json()
      console.log('📨 豆包图生图API响应:', data)

      // 处理响应数据
      if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        const imageUrl = data.data[0].url
        console.log('✅ 获取到图生图结果URL:', imageUrl)
        return imageUrl
      } else if (data.url) {
        const imageUrl = data.url
        console.log('✅ 获取到图生图结果URL:', imageUrl)
        return imageUrl
      } else {
        console.error('❌ 响应格式不正确:', data)
        throw new Error('豆包API未返回图像URL')
      }
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('图生图API调用超时（超过10分钟），请检查网络连接或稍后重试')
      }
      throw error
    }
  }

  // 辅助方法：延迟
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
